import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Buildings,
  Plus,
  Spinner,
  MapPin,
  House,
  ArrowRight,
  Trash,
  Pencil,
  MagnifyingGlass,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ApartmentsPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === "admin";
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({ name: "", address: "", property_id: "" });
  const [editData, setEditData] = useState({ name: "", address: "", property_id: "" });
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [aptPage, setAptPage] = useState(1);
  const [aptPageSize, setAptPageSize] = useState(12);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/customers`);
      setCustomers(res.data);
      if (res.data.length === 1) setSelectedCustomerId(res.data[0].id);
    } catch { /* ignore */ }
  }, []);

  const fetchApartments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/apartments`);
      setApartments(response.data);
    } catch { toast.error(t("common.error_loading")); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => {
    fetchApartments();
    if (isAdmin) fetchCustomers();
    // Fetch properties for dropdown
    axios.get(`${API_URL}/api/properties`).then(r => setProperties(r.data)).catch(() => {});
  }, [fetchApartments, fetchCustomers, isAdmin]);

  const handleCreateApartment = async (e) => {
    e.preventDefault();
    if (isAdmin && !selectedCustomerId) {
      toast.error(t("common.select_customer"));
      return;
    }
    setFormLoading(true);
    try {
      const url = isAdmin
        ? `${API_URL}/api/apartments?customer_id=${selectedCustomerId}`
        : `${API_URL}/api/apartments`;
      await axios.post(url, formData);
      toast.success(t("apartments.created"));
      setDialogOpen(false);
      setFormData({ name: "", address: "", property_id: "" });
      fetchApartments();
    } catch (error) { toast.error(getErrorMessage(error)); }
    finally { setFormLoading(false); }
  };

  const openEditDialog = (apt) => {
    setEditTarget(apt);
    setEditData({ name: apt.name, address: apt.address || "", property_id: apt.property_id || "" });
    setEditDialogOpen(true);
  };

  const handleUpdateApartment = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.put(`${API_URL}/api/apartments/${editTarget.id}`, editData);
      toast.success(t("apartments.updated"));
      setEditDialogOpen(false);
      fetchApartments();
    } catch (error) { toast.error(getErrorMessage(error)); }
    finally { setFormLoading(false); }
  };

  const handleDeleteApartment = async (apartmentId) => {
    try {
      await axios.delete(`${API_URL}/api/apartments/${apartmentId}`);
      toast.success(t("apartments.deleted"));
      fetchApartments();
    } catch { toast.error(t("common.error")); }
  };

  // Search filtering
  const filteredApartments = apartments.filter((a) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const nameMatch = a.name?.toLowerCase().includes(q);
    const addressMatch = a.address?.toLowerCase().includes(q);
    const customerMatch = a.customer_name?.toLowerCase().includes(q);
    return nameMatch || addressMatch || customerMatch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={32} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="apartments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">{t("apartments.title")}</h1>
          <p className="text-slate-500 mt-1">{t("apartments.subtitle")}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-apartment-btn">
              <Plus size={18} className="mr-2" />
              {t("apartments.add")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t("apartments.add_title")}</DialogTitle>
              <DialogDescription>{t("apartments.add_desc")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateApartment}>
              <div className="space-y-4 py-4">
                {isAdmin && (
                  <div className="space-y-2">
                    <Label>{t("common.customer")}</Label>
                    <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
                      <SelectTrigger data-testid="customer-select">
                        <SelectValue placeholder={t("common.select_customer")} />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="name">{t("apartments.name")}</Label>
                  <Input
                    id="name"
                    placeholder={t("apartments.name_placeholder")}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="apartment-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">{t("apartments.address_optional")}</Label>
                  <Input
                    id="address"
                    placeholder={t("apartments.address_placeholder")}
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    data-testid="apartment-address-input"
                  />
                </div>
                {properties.length > 0 && (
                  <div className="space-y-2">
                    <Label>Bina (Opsiyonel)</Label>
                    <select
                      className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                      value={formData.property_id}
                      onChange={e => setFormData({...formData, property_id: e.target.value})}
                      data-testid="apartment-property-select"
                    >
                      <option value="">Bina secin...</option>
                      {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="apartment-submit-btn">
                  {formLoading ? t("common.creating") : t("common.create")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search Bar */}
      {apartments.length > 0 && (
        <div className="relative w-full sm:max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t("apartments.search_placeholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-9"
            data-testid="apartment-search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="apartment-clear-search-btn">
              <X size={16} />
            </button>
          )}
        </div>
      )}

      {/* Apartments Grid */}
      {apartments.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-16 text-center">
            <Buildings size={64} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">{t("apartments.no_apartments")}</h3>
            <p className="text-slate-500 mb-4">{t("apartments.no_apartments_desc")}</p>
          </CardContent>
        </Card>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredApartments.slice((aptPage - 1) * aptPageSize, aptPage * aptPageSize).map((apartment) => (
            <Card key={apartment.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`apartment-card-${apartment.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center">
                      <Buildings size={26} weight="duotone" className="text-indigo-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{apartment.name}</CardTitle>
                      {isAdmin && apartment.customer_name && (
                        <p className="text-sm text-indigo-600 font-medium mt-0.5" data-testid={`apartment-customer-${apartment.id}`}>
                          {apartment.customer_name}
                        </p>
                      )}
                      {apartment.address && (
                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                          <MapPin size={14} />
                          {apartment.address}
                        </p>
                      )}
                      {apartment.property_name && (
                        <p className="text-xs text-violet-600 mt-1">Bina: {apartment.property_name}</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl mb-4">
                  <div className="flex items-center gap-2">
                    <House size={20} className="text-slate-500" />
                    <span className="text-sm text-slate-600">{t("apartments.units_count")}</span>
                  </div>
                  <Badge className="bg-indigo-100 text-indigo-700">{apartment.units_count}</Badge>
                </div>

                <div className="flex gap-2">
                  <Link to={`/apartments/${apartment.id}/units`} className="flex-1">
                    <Button variant="outline" className="w-full" data-testid={`view-units-btn-${apartment.id}`}>
                      {t("apartments.view_units")}
                      <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                  <Button variant="outline" size="icon" className="text-amber-600 hover:text-amber-700"
                    onClick={() => openEditDialog(apartment)} data-testid={`edit-apartment-btn-${apartment.id}`}>
                    <Pencil size={18} />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="icon" className="text-red-600 hover:text-red-700" data-testid={`delete-apartment-btn-${apartment.id}`}>
                        <Trash size={18} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("apartments.delete_title")}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {apartment.name} {t("apartments.delete_confirm")}
                          {t("apartments.delete_warning")}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteApartment(apartment.id)} className="bg-red-600 hover:bg-red-700">
                          {t("common.delete")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Pagination total={filteredApartments.length} page={aptPage} pageSize={aptPageSize} onPageChange={setAptPage} onPageSizeChange={setAptPageSize} />
        </>
      )}

      {/* Edit Apartment Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("apartments.edit_title")}</DialogTitle>
            <DialogDescription>{editTarget?.name} {t("apartments.edit_desc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateApartment}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">{t("apartments.name")}</Label>
                <Input id="edit-name" value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  required data-testid="edit-apartment-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-address">{t("apartments.address")}</Label>
                <Input id="edit-address" value={editData.address}
                  onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                  data-testid="edit-apartment-address-input" />
              </div>
              {properties.length > 0 && (
                <div className="space-y-2">
                  <Label>Bina</Label>
                  <select
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                    value={editData.property_id}
                    onChange={e => setEditData({...editData, property_id: e.target.value})}
                    data-testid="edit-apartment-property-select"
                  >
                    <option value="">Bina secin...</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="edit-apartment-submit-btn">
                {formLoading ? t("common.updating") : t("common.update")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApartmentsPage;
